import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Divider,
    Grid,
    IconButton,
    TextField,
    Alert,
    List,
    ListItem,
    Avatar,
} from '@mui/material';
import {
    Delete,
    ShoppingCart,
    Add,
    Remove,
    ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const CartPage = () => {
    const navigate = useNavigate();
    const {
        cartItems,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
    } = useCart();

    const handleCheckout = () => {
        navigate('/checkout');
    };

    if (cartItems.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <ShoppingCart sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        Your cart is empty
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Looks like you haven't added any items to your cart yet.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/parts')}
                        sx={{ mt: 2 }}
                    >
                        Continue Shopping
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    Shopping Cart
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {getCartCount()} {getCartCount() === 1 ? 'item' : 'items'} in your cart
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Cart Items */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Items</Typography>
                            <Button
                                size="small"
                                color="error"
                                onClick={clearCart}
                                startIcon={<Delete />}
                            >
                                Clear Cart
                            </Button>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <List>
                            {cartItems.map((item, index) => (
                                <React.Fragment key={item.id}>
                                    {index > 0 && <Divider sx={{ my: 2 }} />}
                                    <ListItem
                                        sx={{
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            gap: 2,
                                            p: 2,
                                        }}
                                    >
                                        {/* Product Image */}
                                        <Avatar
                                            variant="rounded"
                                            src={item.thumbnailUrl || item.imageUrl}
                                            sx={{ width: 100, height: 100 }}
                                        >
                                            <ShoppingCart />
                                        </Avatar>

                                        {/* Product Info */}
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {item.displayName || item.partNumber || item.mpn}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {item.manufacturer || 'Unknown Manufacturer'}
                                            </Typography>
                                            {item.package && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Package: {item.package}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Quantity Controls */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 0.5,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                disabled={item.quantity <= 1}
                                            >
                                                <Remove />
                                            </IconButton>
                                            <TextField
                                                size="small"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value) || 1;
                                                    updateQuantity(item.id, value);
                                                }}
                                                inputProps={{
                                                    min: 1,
                                                    style: { textAlign: 'center', width: 50 },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { border: 'none' },
                                                    },
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                            >
                                                <Add />
                                            </IconButton>
                                        </Box>

                                        {/* Price */}
                                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                ${(item.unitPrice || item.price || 0).toFixed(2)} each
                                            </Typography>
                                            <Typography variant="h6" color="primary" fontWeight="bold">
                                                ${((item.unitPrice || item.price || 0) * item.quantity).toFixed(2)}
                                            </Typography>
                                        </Box>

                                        {/* Remove Button */}
                                        <IconButton
                                            color="error"
                                            onClick={() => removeFromCart(item.id)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/parts')}
                        sx={{ mt: 2 }}
                    >
                        Continue Shopping
                    </Button>
                </Grid>

                {/* Order Summary */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                            Order Summary
                        </Typography>
                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    Subtotal ({getCartCount()} items)
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    ${getCartTotal().toFixed(2)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Shipping</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Calculated at checkout
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Tax</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Calculated at checkout
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold">
                                Total
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                ${getCartTotal().toFixed(2)}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleCheckout}
                            sx={{ mb: 2 }}
                        >
                            Proceed to Checkout
                        </Button>

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                Secure checkout with SSL encryption
                            </Typography>
                        </Alert>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default CartPage;
